import classNames from "classnames";
import {
  Key,
  useRef,
  useCallback,
  ReactNode,
  useEffect,
  useState,
} from "react";
import {
  Table,
  TableProps,
  TableColumnsType,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import type {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from "antd/es/table/interface";
import { TableData, FilterSearchOption } from "./tableUtils";

import useFilteredData from "./useFilteredData";
import useTableHeight from "./useTableHeight";
import useScrollStatus from "hooks/useScrollStatus";

import styles from "./style.module.scss";

export type TableRowSelection<T> = NonNullable<TableProps<T>["rowSelection"]>;

export interface VLTableProps<T extends TableData = TableData> {
  columns: TableColumnsType<T>;
  dataSource: T[];
  className?: string;
  loading?: boolean;
  showHeader?: boolean;
  selectedRowKeys?: Key[];
  disabledRowKeys?: Key[];
  showSelectAll?: boolean;
  checkboxDisabledKeys?: Key[];
  checkboxTooltipData?: Record<string, string>;
  filterSearchOption?: FilterSearchOption;
  onSelectAll?: () => void;
  onSelectionChange?: (selectedRowKeys: Key[], selectedRows: T[]) => void;
  onTableFilterChange?: (filters: Record<string, string[] | null>) => void;
}

function VLTable<T extends TableData = TableData>({
  columns,
  dataSource,
  // optional
  className,
  selectedRowKeys,
  filterSearchOption,
  disabledRowKeys,
  checkboxDisabledKeys,
  checkboxTooltipData,
  onTableFilterChange,

  loading = false,
  showHeader = true,
  showSelectAll = false,

  onSelectAll,
  onSelectionChange,
}: VLTableProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tableBodyElement, setTableBodyElement] =
    useState<HTMLDivElement | null>(null);
  const filteredDataSource = useFilteredData<T>(dataSource, filterSearchOption);
  const isSelectionEnabled =
    Array.isArray(selectedRowKeys) && onSelectionChange;

  // Find the actual scrollable body element within the Ant Design table
  useEffect(() => {
    const antTableBody = containerRef.current?.querySelector(
      ".ant-table-body"
    ) as HTMLDivElement;
    if (antTableBody) {
      setTableBodyElement(antTableBody);
    }
  }, [filteredDataSource.length, columns.length]); // Re-run if table content or columns change

  // Track scrollable states for fade effects
  const scrollableStatus = useScrollStatus({ current: tableBodyElement });
  const { x: isScrollableX, y: isScrollableY } = scrollableStatus;

  const tableClassNames = classNames(styles.vlTable, className, {
    [styles.scrollableX]: isScrollableX,
    [styles.scrollableY]: isScrollableY,
  });

  // Handle table filter changes (capture when user changes column filters)
  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<T> | SorterResult<T>[],
      extra: TableCurrentDataSource<T>
    ) => {
      if (onTableFilterChange) {
        // Convert Ant Design filters to our format
        const convertedFilters: Record<string, string[] | null> = {};
        Object.entries(filters).forEach(([key, values]) => {
          convertedFilters[key] =
            Array.isArray(values) && values.length > 0
              ? (values as string[])
              : null;
        });
        onTableFilterChange(convertedFilters);
      }
    },
    [onTableFilterChange]
  );

  const tableHeight = useTableHeight(containerRef, showHeader);

  // Enhanced row selection with disabled row support
  const rowSelection = isSelectionEnabled
    ? {
        selectedRowKeys,
        onChange: onSelectionChange,
        hideSelectAll: !showSelectAll, // Show/hide the Select All checkbox based on showSelectAll prop
        onSelectAll:
          showSelectAll && onSelectAll
            ? (selected: boolean, selectedRows: T[], changeRows: T[]) => {
                if (selected && onSelectAll) {
                  onSelectAll();
                }
              }
            : undefined,
        renderCell: (
          checked: boolean,
          record: T,
          index: number,
          originNode: ReactNode
        ) => {
          const tooltipMessage = checkboxTooltipData?.[record.key as string];

          if (tooltipMessage) {
            return (
              <Tooltip
                data-testid="vl-table-checkbox-tooltip"
                title={tooltipMessage}
                placement="top"
              >
                {originNode}
              </Tooltip>
            );
          }

          return originNode;
        },
        getCheckboxProps: (record: T) => ({
          disabled:
            checkboxDisabledKeys?.includes(record.key as Key) ||
            disabledRowKeys?.includes(record.key as Key),
        }),
      }
    : undefined;

  // Custom row className to handle disabled styling
  const getRowClassnames = (record: T) => {
    return classNames({
      [styles.disabledRow]: disabledRowKeys?.includes(record.key as Key),
      [styles.checkboxDisabledRow]: checkboxDisabledKeys?.includes(
        record.key as Key
      ),
    });
  };

  return (
    <div ref={containerRef} className={styles.tableContainer}>
      <Table<T>
        size="small"
        columns={columns}
        loading={loading}
        data-testid="vl-table"
        showHeader={showHeader}
        className={tableClassNames}
        rowSelection={rowSelection}
        dataSource={filteredDataSource}
        rowClassName={getRowClassnames}
        scroll={{ x: true, y: tableHeight }}
        pagination={false}
        onChange={handleTableChange}
      />
    </div>
  );
}

export default VLTable;
